import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Link2, ShieldCheck, Users, Workflow, AlertTriangle, LayoutDashboard } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/plano-roteador-grupos")({
  head: () => ({
    meta: [
      { title: "Admin · Plano Roteador de Grupos WhatsApp" },
      { name: "description", content: "Plano do roteador inteligente de grupos de WhatsApp da FEO, sem ferramenta externa." },
      { property: "og:title", content: "Admin · Roteador de Grupos WhatsApp" },
      { property: "og:description", content: "Rotação automática de grupos por cota, controlada no painel da FEO." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/plano-roteador-grupos"><Plano /></AdminGate>,
});

const situacao: [string, string][] = [
  ["/black-lista", "Grupo principal fixo: chat.whatsapp.com/C2eCN8wDITf3JUK0ySbIy0"],
  ["/black-lista-entregas", "Grupo próprio fixo: chat.whatsapp.com/D56l9SFkmYf2C2zkITwgeL"],
  ["Meta da ação", "5.000 leads · cada grupo comporta no máximo 1.024 pessoas"],
  ["Hoje", "Contagem de membros só no cabeçalho do grupo, olhada por um admin da equipe"],
];

const fluxo: [string, string][] = [
  ["1 · Link inteligente", "As páginas deixam de usar o link fixo do WhatsApp e passam a usar links da própria FEO: /entrar/black e /entrar/black-entregas."],
  ["2 · Fila de grupos", "No painel cadastramos os grupos de cada fila em ordem (Grupo 1, 2, 3...), cada um com o seu link e uma cota."],
  ["3 · Cota de segurança", "Cota padrão de 850 leads por grupo, abaixo do teto de 1.024, para cobrir quem entra por outros caminhos."],
  ["4 · Contagem", "Cada lead qualificado que conclui a inscrição soma 1 no grupo ativo daquela fila."],
  ["5 · Virada automática", "Bateu a cota, o próximo grupo vira o ativo na hora. Ninguém precisa trocar link no site."],
  ["6 · Virada manual", "Botão \"Virar para o próximo grupo agora\" no painel, para quando a equipe quiser antecipar."],
  ["7 · Alerta", "Aviso no painel quando o grupo ativo passar de 80% da cota e quando restar só 1 grupo cadastrado na fila."],
];

const painel = [
  "Lista de grupos por fila (principal e entregas) com link, cota e status (ativo, na fila, cheio)",
  "Barra de progresso por grupo, ex.: 720 / 850",
  "Total de leads enviados por fila e por dia",
  "Cadastro de novo grupo, edição de cota e reordenação da fila",
  "Botão de virada manual e histórico de viradas (quando, quem e por quê)",
];

const vantagens = [
  "Custo zero: sem mensalidade de JoinVip, Devzapp, SendFlow ou similares",
  "Nenhum robô ou chip conectado ao WhatsApp · nenhum número do CRM ou das vendedoras envolvido",
  "Tudo dentro do painel da FEO, junto com leads, vendedoras e CRMs",
  "Funciona para a Black e para qualquer ação futura com grupos",
];

const limites = [
  "O sistema conta leads enviados ao grupo, não membros reais: quem não clica, sai ou entra por link compartilhado não aparece",
  "Por isso a cota fica abaixo de 1.024 e a equipe confere o número real no cabeçalho do grupo de tempos em tempos",
  "Os grupos precisam ser criados antes pela equipe no WhatsApp e cadastrados no painel com folga (sempre 1 grupo reserva na fila)",
];

const passos = [
  "Equipe cria os grupos extras da Black no WhatsApp (estimativa: 5 a 6 para a lista principal e 1 a 2 para entregas)",
  "Construção do roteador, dos links /entrar/black e /entrar/black-entregas e do módulo no painel",
  "Cadastro dos grupos e cotas no painel",
  "Troca dos links das LPs /black-lista e /black-lista-entregas para os links inteligentes",
  "Teste com inscrições reais e conferência da virada",
  "Publicar o site",
];

function Card({ title, icon: Icon, children }: { title: string; icon: typeof Users; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <h2 className="flex items-center gap-2 text-lg font-bold text-white mb-4"><Icon className="h-5 w-5 text-[#f5b022]" />{title}</h2>
      {children}
    </section>
  );
}

function Lista({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i} className="flex gap-2 text-sm text-white/80"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#10b981]" />{i}</li>
      ))}
    </ul>
  );
}

function Plano() {
  return (
    <div className="min-h-screen px-4 py-12">
      <div className="mx-auto max-w-4xl space-y-6">
        <Link to="/admin/planos" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> Projetos/Implementações</Link>
        <header>
          <p className="text-xs uppercase tracking-widest text-[#8b9bb5]">Futuro · aguardando aprovação da equipe</p>
          <h1 className="text-3xl font-bold text-white mt-1">Roteador Inteligente de Grupos de WhatsApp</h1>
          <p className="text-white/70 mt-2">Rotação automática dos grupos por cota, feita dentro do site e do painel da FEO, sem ferramenta externa e sem conectar nenhum número ao WhatsApp.</p>
        </header>

        <Card title="Situação atual" icon={Users}>
          <dl className="grid gap-3 sm:grid-cols-2">
            {situacao.map(([k, v]) => (
              <div key={k} className="rounded-lg bg-white/5 p-3"><dt className="text-xs text-white/50">{k}</dt><dd className="text-sm text-white mt-1 break-words">{v}</dd></div>
            ))}
          </dl>
        </Card>

        <Card title="Como funciona" icon={Workflow}>
          <ol className="space-y-3">
            {fluxo.map(([k, v]) => (
              <li key={k} className="rounded-lg bg-white/5 p-3"><p className="text-sm font-semibold text-[#f5b022]">{k}</p><p className="text-sm text-white/80 mt-1">{v}</p></li>
            ))}
          </ol>
        </Card>

        <Card title="Módulo no painel admin" icon={LayoutDashboard}><Lista items={painel} /></Card>
        <Card title="Vantagens" icon={ShieldCheck}><Lista items={vantagens} /></Card>

        <Card title="Limites e cuidados" icon={AlertTriangle}>
          <ul className="space-y-2">
            {limites.map((i) => <li key={i} className="flex gap-2 text-sm text-white/80"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#f5b022]" />{i}</li>)}
          </ul>
        </Card>

        <Card title="Próximos passos (quando aprovado)" icon={Link2}>
          <ol className="list-decimal pl-5 space-y-2 text-sm text-white/80">{passos.map((p) => <li key={p}>{p}</li>)}</ol>
          <p className="text-xs text-white/50 mt-4">Descartado: chip sentinela via Evolution API. Regra fixa: nunca usar os números do CRM ou das vendedoras em automações de grupos.</p>
        </Card>
      </div>
    </div>
  );
}
